import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtNGN, formatErr } from "@/lib/api";
import { ReviewStatusBadge, EscalatedBadge, fmtWhen } from "@/components/ReviewBits";
import { ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [["open", "Open"], ["pending_review", "Pending Review"], ["in_review", "In Review"], ["approved", "Approved"], ["declined", "Declined"], ["all", "All"]];
const OPEN = ["pending_review", "in_review"];

export function useQueue(params) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/reviews/queue", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(formatErr(e?.response?.data?.detail) || e.message));
  useEffect(() => { load(); }, [JSON.stringify(params)]); // eslint-disable-line
  return [rows, load];
}

export default function QueueTable({ rows, base, showReviewer, extra }) {
  const [tab, setTab] = useState("open");
  const filtered = useMemo(() => (rows || []).filter((r) => tab === "all" || (tab === "open" ? OPEN.includes(r.review_status) : r.review_status === tab)), [rows, tab]);
  const count = (k) => (rows || []).filter((r) => k === "all" || (k === "open" ? OPEN.includes(r.review_status) : r.review_status === k)).length;

  return (
    <div className="card" data-testid="review-queue">
      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`btn-ghost text-sm ${tab === k ? "!bg-[#0A3622] !text-white" : ""}`} data-testid={`queue-tab-${k}`}>
            {label} <span className="ml-1.5 font-mono text-[11px] opacity-80">{count(k)}</span>
          </button>
        ))}
      </div>
      {rows === null ? <p className="text-sm text-[#4F5D4F] py-8 text-center">Loading…</p>
        : filtered.length === 0 ? <p className="text-sm text-[#4F5D4F] py-8 text-center" data-testid="queue-empty">No applications in this view.</p>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-[#4F5D4F] border-b border-[#E5E3DB]">
                  <th className="py-2 pr-3">Applicant</th><th className="py-2 pr-3">Loan type</th><th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pr-3">Status</th>{showReviewer && <th className="py-2 pr-3">Reviewer</th>}<th className="py-2 pr-3">Assigned</th><th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-[#F0EFEA] hover:bg-[#F9F7F4]" data-testid={`queue-row-${r.id}`}>
                    <td className="py-3 pr-3"><div className="font-medium">{r.applicant.name || "—"}</div><div className="text-xs text-[#4F5D4F]">{r.applicant.email}</div></td>
                    <td className="py-3 pr-3">{r.loan_type}<div className="text-xs font-mono text-[#4F5D4F]">{r.ref}</div></td>
                    <td className="py-3 pr-3 text-right font-mono">{fmtNGN(r.amount)}</td>
                    <td className="py-3 pr-3"><div className="flex flex-wrap gap-1"><ReviewStatusBadge status={r.review_status} testid={`queue-status-${r.id}`} />{r.escalated && <EscalatedBadge testid={`queue-escalated-${r.id}`} />}</div></td>
                    {showReviewer && <td className="py-3 pr-3">{extra ? extra(r) : r.reviewer_name || "—"}</td>}
                    <td className="py-3 pr-3 text-xs text-[#4F5D4F]">{fmtWhen(r.assigned_at || r.queued_at)}</td>
                    <td className="py-3 text-right">
                      {r.review_status !== "pending_assignment" && (
                        <Link to={`${base}/${r.kind}/${r.id}`} className="btn-outline !py-1.5 !px-3 text-xs" data-testid={`queue-open-${r.id}`}>Review <ArrowRight size={12} /></Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
